/**
 * SendDocumentDialog — sender chooses signing mode per signer, satisfies
 * Wisconsin marital-property safeguards (FR-12 / FR-13), and kicks off the
 * envelope via POST /api/documents/{id}/send.
 *
 * FR-13: a spouse who signs by email is verified by DocuSign Phone
 * Authentication (the spouse picks SMS or a call) on the mobile number they
 * gave when their partner confirmed marital status. The dialog only tells the
 * sender which number is used (last 4 digits) — it no longer collects a
 * sender-chosen Access Code.
 *
 * Design spec: docs/design/DOCUSIGN_UI_DESIGN.md §3
 * API contract:  docs/architecture/DOCUSIGN_MIGRATION_DESIGN.md §12.1
 *
 * Signers: pass `signers` explicitly, or omit it and the dialog uses the
 * `planned_signers` from send-readiness (buyer, seller and, in a
 * marital-property state, their linked spouses). Likewise `showMaritalAck`
 * falls back to readiness `marital_property_scope`.
 *
 * Lead-based paint: when send-readiness says the disclosure is stale or was
 * rendered without signature lines (`lead_paint_regeneration`), the seller
 * gets a Regenerate button here; the other party reads, in the blockers, that
 * the seller has to do it (QA run 2026-10-21, R8-12).
 *
 * Reuses TourRequestModal's shell (`modal-overlay` / `modal-content` etc.)
 * for consistency with the rest of the app's modal language.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchSendReadiness, sendDocumentEnvelope } from '../../agents/docusignAgent';
import { regenerateDisclosure } from '../../agents/leadPaintDisclosureAgent';
import './SendDocumentDialog.css';

const COPY = {
  title: 'Send for signature',
  signersHeader: (n) => `Signers (${n})`,
  modeLabel: 'Signing mode',
  modeEmbedded: 'Sign in cutthe6.com',
  modeRemote: 'Send via email',
  hintEmbedded: "They'll click Sign Now in cutthe6.com.",
  hintRemote: (email) => `They'll receive a DocuSign email at ${email || 'their address'}.`,
  hintRemoteLocked: 'No cutthe6.com account — they must sign by email.',
  phoneAuthNote: (last4) =>
    `Phone check: DocuSign will text or call •••${last4} before they can sign.`,
  phoneAuthNoteNoDigits:
    'Phone check: DocuSign will text or call their mobile number before they can sign.',
  phoneAuthMissing: (name) =>
    `${name} needs a mobile number for the identity check — ask them to update their marital status.`,
  maritalBanner:
    "Wisconsin law generally requires a married seller's spouse to sign any conveyance of the couple's homestead, even if the spouse is not on title, and may require spousal signatures for other marital property. Confirm all required spouses are listed below.",
  maritalDisclaimer:
    'General information, not legal advice. Consult a licensed Wisconsin real estate attorney about your situation.',
  maritalCheckbox:
    'All legally required signers (including spouses where applicable) are included.',
  cancel: 'Cancel',
  sendIdle: (n) => `Send (${n})`,
  sendLoading: 'Sending…',
  toastSuccess: (n) => `Sent to ${n} signer${n === 1 ? '' : 's'}.`,
  errorOutage:
    'Signing provider temporarily unavailable — please retry. No partial envelope was created.',
  errorAlreadySent:
    'This document was already sent. Reload to see the latest status.',
  errorGeneric: "We couldn't send this document. Please try again.",
  errorNetwork: 'Network error. Check your connection and try again.',
  loadingSigners: 'Loading signers…',
  emptyTitle: 'No signers configured',
  emptyBody:
    'This document has no signers. Close this dialog and regenerate the document.',
  close: 'Close',
  readinessTitle: 'Cannot send yet',
  readinessFallback: 'Unable to verify send readiness right now. Please retry.',
  regenerateHint:
    'Regenerating prepares the disclosure again from the answers already recorded. If anything has changed, update your answers first.',
  regenerateIdle: 'Regenerate disclosure',
  regenerateLoading: 'Regenerating…',
  regenerateError:
    "We couldn't prepare a signable lead-based paint disclosure. Please try again, or contact support if it keeps happening.",
};

const SPOUSE_ROLES = new Set(['buyer_spouse', 'seller_spouse']);
const MARITAL_PROPERTY_STATES = new Set(['WI']);

const ROLE_LABELS = {
  buyer: 'Buyer',
  seller: 'Seller',
  buyer_spouse: "Buyer's spouse",
  seller_spouse: "Seller's spouse",
};

const formatRole = (role) =>
  ROLE_LABELS[role] || (role ? role.replace(/_/g, ' ') : 'Signer');

const defaultModeFor = (signer) => {
  if (signer?.person_id != null && signer?.email_verified !== false) {
    return 'embedded';
  }
  return 'remote';
};

const isModeLocked = (signer) =>
  signer?.person_id == null || signer?.email_verified === false;

const requiresPhoneAuth = (signer, mode, propertyState) =>
  MARITAL_PROPERTY_STATES.has((propertyState || '').toUpperCase()) &&
  SPOUSE_ROLES.has(signer?.role) &&
  mode === 'remote';

const hasPhoneFor = (signer) => signer?.has_phone === true || !!signer?.phone_last4;

const SignerModeToggle = ({ signer, mode, locked, onChange }) => {
  const groupRef = useRef(null);
  const groupId = `mode-${signer.signer_id}`;

  const handleKey = (e) => {
    if (locked) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      onChange('embedded');
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      onChange('remote');
    }
  };

  return (
    <div
      ref={groupRef}
      className="send-doc-mode-toggle"
      role="radiogroup"
      aria-label={`Signing mode for ${signer.name || formatRole(signer.role)}`}
      onKeyDown={handleKey}
    >
      {[
        { value: 'embedded', label: COPY.modeEmbedded },
        { value: 'remote', label: COPY.modeRemote },
      ].map((opt) => {
        const selected = mode === opt.value;
        const disabled = locked && opt.value !== 'remote';
        return (
          <button
            key={opt.value}
            type="button"
            id={`${groupId}-${opt.value}`}
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            disabled={disabled}
            className={
              'send-doc-mode-toggle__option' +
              (selected ? ' send-doc-mode-toggle__option--selected' : '') +
              (disabled ? ' send-doc-mode-toggle__option--disabled' : '')
            }
            title={
              disabled
                ? "This signer has no cutthe6.com account, so they'll receive a DocuSign email."
                : undefined
            }
            onClick={() => !disabled && onChange(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

const SignerRow = ({ signer, mode, onModeChange, propertyState }) => {
  const locked = isModeLocked(signer);
  const needsPhoneAuth = requiresPhoneAuth(signer, mode, propertyState);
  const hasPhone = hasPhoneFor(signer);

  let hint;
  if (locked) hint = COPY.hintRemoteLocked;
  else if (mode === 'embedded') hint = COPY.hintEmbedded;
  else hint = COPY.hintRemote(signer.email);

  return (
    <div className="send-doc-signer-row">
      <div className="send-doc-signer-identity">
        <div className="send-doc-signer-name">{signer.name || 'Unnamed signer'}</div>
        <div className="send-doc-signer-meta">
          <span>{signer.email || '—'}</span>
          <span aria-hidden="true">·</span>
          <span>{formatRole(signer.role)}</span>
        </div>
      </div>

      <div className="send-doc-signer-controls">
        <span className="send-doc-mode-label">{COPY.modeLabel}:</span>
        <SignerModeToggle
          signer={signer}
          mode={mode}
          locked={locked}
          onChange={(next) => onModeChange(signer.signer_id, next)}
        />
        <p className="send-doc-mode-hint">↳ {hint}</p>
      </div>

      {needsPhoneAuth && (
        <p
          className={
            'send-doc-phone-auth' + (hasPhone ? '' : ' send-doc-phone-auth--missing')
          }
          data-testid={`phone-auth-${signer.signer_id}`}
        >
          <span aria-hidden="true">🔒</span>
          <span>
            {hasPhone
              ? (signer.phone_last4
                ? COPY.phoneAuthNote(signer.phone_last4)
                : COPY.phoneAuthNoteNoDigits)
              : COPY.phoneAuthMissing(signer.name || formatRole(signer.role))}
          </span>
        </p>
      )}
    </div>
  );
};

const SendDocumentDialog = ({
  open,
  documentId,
  documentType,
  propertyAddress,
  propertyState,
  signers: signersProp,
  showMaritalAck: showMaritalAckProp,
  onClose,
  onSent,
  onRegenerated,
}) => {
  const titleRef = useRef(null);

  // Per-signer selected mode.
  const [modes, setModes] = useState({});
  const [maritalAck, setMaritalAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [sendReadiness, setSendReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessError, setReadinessError] = useState(null);
  // Bumped after a lead-paint regeneration to re-check readiness.
  const [readinessCheck, setReadinessCheck] = useState(0);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState(null);

  const signersFromReadiness = !Array.isArray(signersProp);
  const signers = useMemo(() => {
    if (!signersFromReadiness) return signersProp;
    const planned = sendReadiness?.planned_signers;
    if (!Array.isArray(planned)) return null;
    return planned.map((p) => ({
      signer_id: p.signer_id,
      person_id: p.person_id ?? p.signer_id,
      email: p.email || '',
      // No cutthe6.com login -> the toggle locks to email signing.
      email_verified: p.has_account !== false,
      name: p.name || formatRole(p.role),
      role: p.role,
      // Spouse identity check (DocuSign Phone Authentication).
      has_phone: p.has_phone === true,
      phone_last4: p.phone_last4 || null,
    }));
  }, [signersFromReadiness, signersProp, sendReadiness]);
  const showMaritalAck = showMaritalAckProp ?? !!sendReadiness?.marital_property_scope;
  const signersLoading = signersFromReadiness && signers == null && readinessLoading;

  // Initialise mode defaults whenever signer list changes.
  useEffect(() => {
    if (!open || !Array.isArray(signers)) return;
    setModes((prev) => {
      const next = { ...prev };
      signers.forEach((s) => {
        if (next[s.signer_id] == null) next[s.signer_id] = defaultModeFor(s);
      });
      return next;
    });
  }, [open, signers]);

  useEffect(() => {
    let active = true;
    if (!open || !documentId) {
      setSendReadiness(null);
      setReadinessError(null);
      setReadinessLoading(false);
      return () => {
        active = false;
      };
    }

    setReadinessLoading(true);
    setReadinessError(null);
    fetchSendReadiness(documentId)
      .then((payload) => {
        if (!active) return;
        setSendReadiness(payload);
      })
      .catch((err) => {
        if (!active) return;
        setReadinessError(err?.message || COPY.readinessFallback);
      })
      .finally(() => {
        if (!active) return;
        setReadinessLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, documentId, readinessCheck]);

  // Reset transient state on close.
  useEffect(() => {
    if (!open) {
      setSubmitError(null);
      setSubmitting(false);
      setMaritalAck(false);
      setRegenerateError(null);
    }
  }, [open]);

  // Move focus to the dialog title on open for SR + keyboard users.
  useEffect(() => {
    if (open && titleRef.current) {
      titleRef.current.focus();
    }
  }, [open]);

  const handleModeChange = (signerId, mode) => {
    setModes((prev) => ({ ...prev, [signerId]: mode }));
  };

  // ESC closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  const validation = useMemo(() => {
    if (!Array.isArray(signers) || signers.length === 0) {
      return { valid: false, firstUnmet: 'Add at least one signer to send.' };
    }
    if (showMaritalAck && !maritalAck) {
      return { valid: false, firstUnmet: 'Confirm all required signers are included.' };
    }
    if (readinessLoading) {
      return { valid: false, firstUnmet: 'Checking send readiness…' };
    }
    if (readinessError) {
      return { valid: false, firstUnmet: readinessError };
    }
    if (sendReadiness && sendReadiness.is_ready === false) {
      const first = Array.isArray(sendReadiness.reasons) && sendReadiness.reasons.length > 0
        ? sendReadiness.reasons[0]
        : 'Resolve send blockers before continuing.';
      return { valid: false, firstUnmet: first };
    }
    for (const s of signers) {
      const mode = modes[s.signer_id] || defaultModeFor(s);
      if (mode !== 'embedded' && mode !== 'remote') {
        return { valid: false, firstUnmet: `Pick a signing mode for ${s.name}.` };
      }
      if (mode === 'remote' && !s.email) {
        return { valid: false, firstUnmet: `${s.name || 'A signer'} needs an email to receive the envelope.` };
      }
      if (requiresPhoneAuth(s, mode, propertyState) && !hasPhoneFor(s)) {
        return {
          valid: false,
          firstUnmet: COPY.phoneAuthMissing(s.name || formatRole(s.role)),
        };
      }
    }
    return { valid: true, firstUnmet: null };
  }, [
    signers,
    modes,
    propertyState,
    showMaritalAck,
    maritalAck,
    sendReadiness,
    readinessLoading,
    readinessError,
  ]);

  const signerCount = signers?.length ?? 0;
  const busy = submitting || regenerating;
  const leadPaintRegeneration = sendReadiness?.lead_paint_regeneration || null;

  const handleRegenerate = async () => {
    if (!leadPaintRegeneration?.can_regenerate || regenerating) return;
    setRegenerating(true);
    setRegenerateError(null);
    try {
      await regenerateDisclosure(leadPaintRegeneration.offer_id);
      onRegenerated?.();
      setReadinessCheck((n) => n + 1);
    } catch (err) {
      setRegenerateError(err?.message || COPY.regenerateError);
    } finally {
      setRegenerating(false);
    }
  };

  const handleSend = async () => {
    if (!validation.valid || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    const payload = {
      signers: signers.map((s) => {
        const mode = modes[s.signer_id] || defaultModeFor(s);
        return {
          signer_id: s.signer_id,
          role: s.role,
          signing_mode: mode,
        };
      }),
      sender_completeness_ack: showMaritalAck ? !!maritalAck : undefined,
    };
    try {
      const envelope = await sendDocumentEnvelope(documentId, payload);
      onSent?.(envelope);
    } catch (err) {
      const message = String(err?.message || '');
      if (/503|temporarily unavailable|retry/i.test(message)) {
        setSubmitError({ kind: 'outage', text: COPY.errorOutage });
      } else if (/already (was )?sent|409/i.test(message)) {
        setSubmitError({ kind: 'alreadySent', text: COPY.errorAlreadySent });
      } else if (/network|fetch|abort/i.test(message)) {
        setSubmitError({ kind: 'network', text: COPY.errorNetwork });
      } else {
        setSubmitError({ kind: 'generic', text: message || COPY.errorGeneric });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const subtitleParts = [documentType, propertyAddress].filter(Boolean);

  return (
    <div
      className="modal-overlay send-doc-dialog__overlay"
      onClick={() => !submitting && onClose?.()}
    >
      <div
        className="modal-content send-doc-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-doc-title"
        aria-busy={submitting || undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header send-doc-dialog__header">
          <div>
            <h2
              id="send-doc-title"
              ref={titleRef}
              tabIndex={-1}
              className="send-doc-dialog__title"
            >
              {COPY.title}
            </h2>
            {subtitleParts.length > 0 && (
              <p className="send-doc-dialog__subtitle">
                {subtitleParts.join(' · ')}
              </p>
            )}
          </div>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label="Close send dialog"
            disabled={submitting}
          >
            ×
          </button>
        </div>

        <div className="modal-body send-doc-dialog__body">
          {showMaritalAck && (
            <section
              className="send-doc-marital-ack"
              aria-labelledby="marital-ack-heading"
            >
              <div className="send-doc-marital-ack__icon" aria-hidden="true">⚠</div>
              <div className="send-doc-marital-ack__content">
                <h3
                  id="marital-ack-heading"
                  className="send-doc-marital-ack__heading"
                >
                  Marital-property notice
                </h3>
                <p>{COPY.maritalBanner}</p>
                <p className="send-doc-marital-ack__disclaimer">{COPY.maritalDisclaimer}</p>
                <label className="send-doc-marital-ack__checkbox">
                  <input
                    type="checkbox"
                    id="marital-ack-checkbox"
                    checked={maritalAck}
                    onChange={(e) => setMaritalAck(e.target.checked)}
                  />
                  <span>
                    {COPY.maritalCheckbox}{' '}
                    <span className="send-doc-marital-ack__required">*required</span>
                  </span>
                </label>
              </div>
            </section>
          )}

          {signersLoading ? (
            <p className="send-doc-dialog__loading">{COPY.loadingSigners}</p>
          ) : signerCount === 0 ? (
            <div className="send-doc-empty">
              <span className="send-doc-empty__icon" aria-hidden="true">👥</span>
              <h3>{COPY.emptyTitle}</h3>
              <p>{COPY.emptyBody}</p>
            </div>
          ) : (
            <>
              <h3 className="send-doc-dialog__signers-header">
                {COPY.signersHeader(signerCount)}
              </h3>
              <div className="send-doc-dialog__signers">
                {signers.map((signer) => (
                  <SignerRow
                    key={signer.signer_id}
                    signer={signer}
                    mode={modes[signer.signer_id] || defaultModeFor(signer)}
                    onModeChange={handleModeChange}
                    propertyState={propertyState}
                  />
                ))}
              </div>
            </>
          )}

          {submitError && (
            <div
              className="send-doc-dialog__error"
              role="alert"
            >
              {submitError.text}
              {submitError.kind === 'alreadySent' && (
                <button
                  type="button"
                  className="send-doc-dialog__error-action"
                  onClick={() => onClose?.()}
                >
                  Reload
                </button>
              )}
            </div>
          )}

          {sendReadiness && sendReadiness.is_ready === false && Array.isArray(sendReadiness.reasons) && sendReadiness.reasons.length > 0 && (
            <div className="send-doc-dialog__error send-doc-dialog__error--readiness" role="alert">
              <strong>{COPY.readinessTitle}:</strong>
              <ul>
                {sendReadiness.reasons.map((reason, idx) => (
                  <li key={`${reason}-${idx}`}>{reason}</li>
                ))}
              </ul>
              {leadPaintRegeneration?.can_regenerate && (
                <div className="send-doc-dialog__regenerate">
                  <p className="send-doc-dialog__regenerate-hint">{COPY.regenerateHint}</p>
                  <button
                    type="button"
                    className="btn-secondary send-doc-dialog__regenerate-btn"
                    onClick={handleRegenerate}
                    disabled={busy}
                    aria-busy={regenerating || undefined}
                  >
                    {regenerating ? COPY.regenerateLoading : COPY.regenerateIdle}
                  </button>
                </div>
              )}
            </div>
          )}

          {regenerateError && (
            <div className="send-doc-dialog__error" role="alert">
              {regenerateError}
            </div>
          )}

          {!validation.valid && signerCount > 0 && (
            <p
              id="send-doc-unmet"
              className="send-doc-dialog__unmet"
            >
              {validation.firstUnmet}
            </p>
          )}
        </div>

        <div className="modal-footer send-doc-dialog__footer">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {COPY.cancel}
          </button>
          <button
            type="button"
            className="btn-primary send-doc-dialog__send"
            onClick={handleSend}
            disabled={!validation.valid || busy || signerCount === 0}
            aria-busy={submitting || undefined}
            aria-describedby={!validation.valid ? 'send-doc-unmet' : undefined}
          >
            {submitting ? COPY.sendLoading : COPY.sendIdle(signerCount)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendDocumentDialog;
